import React, { useEffect, useState, useCallback } from 'react';
import { SlidersHorizontal, Save, AlertTriangle } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { schoolApi } from '../../api/school';
import { PageHeader, Card, LoadingState } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { SchoolAccountSearch } from '../../components/school/SchoolAccountSearch';
import { toast } from 'sonner';

function AccountInput({ value, onChange, orgId, testid }) {
  return <SchoolAccountSearch orgId={orgId} value={value} onChange={onChange} testid={testid} />;
}

export default function FinanceSettingsPage() {
  const orgId = useOrgId();
  const [settings, setSettings] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        schoolApi.getFinanceSettings(orgId), schoolApi.listFeeGroups(orgId),
      ]);
      setSettings(s); setGroups(g);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const setPM = (k, v) => setSettings((s) => ({ ...s, payment_method_accounts: { ...s.payment_method_accounts, [k]: v } }));
  const setFG = (gid, v) => setSettings((s) => ({ ...s, fee_group_accounts: { ...s.fee_group_accounts, [gid]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await schoolApi.saveFinanceSettings({ organization_id: orgId, ...settings });
      toast.success('Finance settings saved'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="space-y-6"><PageHeader icon={SlidersHorizontal} title="Finance Settings" /><LoadingState /></div>;

  const anyMissing = !settings.guardian_receivable_code || !settings.waiver_expense_code ||
    Object.values(settings.payment_method_accounts || {}).some((v) => !v) ||
    groups.some((g) => !(settings.fee_group_accounts[g.id] || g.revenue_account_code));

  return (
    <div className="space-y-6" data-testid="finance-settings-page">
      <PageHeader icon={SlidersHorizontal} title="Finance Settings" subtitle="Map accounting codes used when posting fees"
        actions={<Button onClick={save} disabled={saving} data-testid="finance-settings-save"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}</Button>} />
      {anyMissing && (
        <Card className="p-3 flex items-center gap-2 text-amber-700 bg-amber-50 border-amber-200">
          <AlertTriangle className="w-4 h-4" /><span className="text-sm">Some mappings are missing. Posting will be blocked until every required account is mapped.</span>
        </Card>
      )}
      <Card className="p-4 space-y-4">
        <p className="font-semibold">Core accounts</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Guardian receivable account</Label><AccountInput value={settings.guardian_receivable_code} onChange={(v) => setSettings((s) => ({ ...s, guardian_receivable_code: v }))} orgId={orgId} testid="fs-receivable" /><p className="text-xs text-muted-foreground mt-1">Global fallback. Each guardian's own account is used first when posting; this applies only to guardians without an account.</p></div>
          <div><Label>Waiver expense account</Label><AccountInput value={settings.waiver_expense_code} onChange={(v) => setSettings((s) => ({ ...s, waiver_expense_code: v }))} orgId={orgId} testid="fs-waiver" /></div>
        </div>
      </Card>
      <Card className="p-4 space-y-4">
        <p className="font-semibold">Payment method accounts (cash / bank)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Cash</Label><AccountInput value={settings.payment_method_accounts.cash} onChange={(v) => setPM('cash', v)} orgId={orgId} testid="fs-cash" /></div>
          <div><Label>Bank transfer</Label><AccountInput value={settings.payment_method_accounts.bank_transfer} onChange={(v) => setPM('bank_transfer', v)} orgId={orgId} testid="fs-bank" /></div>
          <div><Label>Cheque</Label><AccountInput value={settings.payment_method_accounts.cheque} onChange={(v) => setPM('cheque', v)} orgId={orgId} testid="fs-cheque" /></div>
        </div>
      </Card>
      <Card className="p-4 space-y-4">
        <p className="font-semibold">Fee group revenue accounts</p>
        {groups.length === 0 ? <p className="text-sm text-muted-foreground">No fee groups yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groups.map((g) => (
              <div key={g.id}><Label>{g.name}</Label>
                <AccountInput value={settings.fee_group_accounts[g.id] ?? g.revenue_account_code ?? ''} onChange={(v) => setFG(g.id, v)} orgId={orgId} testid={`fs-group-${g.id}`} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
